/* eslint-disable no-use-before-define */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    TextField,
    DialogActions,
    Grid,
    Typography,
    TablePagination,
    Paper,
    CircularProgress,
    Chip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import Swal from 'sweetalert2';
import { useTravelPlans } from 'hooks/api/travel-plan/useTravelPlans';
import { logger } from 'utils/environment/log';
import { globalColor } from 'views/ConstantColors';

const swalConfig = {
    customClass: {
        container: 'my-swal-container',
        popup: 'my-swal-popup'
    }
};

const styles = `
    .my-swal-container {
        z-index: 9999 !important;
        position: fixed !important;
    }
    .my-swal-popup {
        z-index: 10000 !important;
    }
    .swal2-backdrop-show {
        z-index: 9998 !important;
    }
`;

const initialFilters = {
    departureTime: '',
    returnTime: '',
    type: ''
};

const TripPlanSearchDialog = ({ open, onClose, onOpenTravelPlanForm, onOpenTravelRequestForm }) => {
    const { searchPlans, loading } = useTravelPlans();
    const [searchResults, setSearchResults] = useState({ content: [], totalElements: 0 });
    const [filters, setFilters] = useState(initialFilters);
    const [searchPerformed, setSearchPerformed] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [localSearch, setLocalSearch] = useState('');
    const [filteredResults, setFilteredResults] = useState({ content: [], totalElements: 0 });

    useEffect(() => {
        const styleElement = document.createElement('style');
        styleElement.innerHTML = styles;
        document.head.appendChild(styleElement);

        return () => {
            document.head.removeChild(styleElement);
        };
    }, []);

    const getCurrentMonthYear = () => {
        const now = new Date();
        return {
            date: now,
            month: now.getMonth(),
            year: now.getFullYear(),
            formatted: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
        };
    };

    const isTripsInPast = (departureTime, returnTime) => {
        if (!departureTime || !returnTime) return false;
        const currentDate = new Date();
        const returnDate = new Date(returnTime);
        returnDate.setHours(23, 59, 59, 999);
        return returnDate < currentDate;
    };

    const filterOutPastTrips = (plans) => {
        const currentDate = new Date();
        return plans.filter((plan) => {
            const returnDate = new Date(plan.returnTime);
            returnDate.setHours(23, 59, 59, 999);
            return returnDate >= currentDate;
        });
    };

    useEffect(() => {
        if (searchResults.content) {
            const searchValue = localSearch.toLowerCase().trim();
            const filtered = filterOutPastTrips(searchResults.content).filter(
                (plan) =>
                    plan.planName?.toLowerCase().includes(searchValue) ||
                    plan.venue?.toLowerCase().includes(searchValue) ||
                    formatDate(plan.departureTime).toLowerCase().includes(searchValue) ||
                    formatDate(plan.returnTime).toLowerCase().includes(searchValue) ||
                    plan.type?.toLowerCase().includes(searchValue)
            );
            setFilteredResults({
                content: filtered,
                totalElements: filtered.length
            });
        }
    }, [searchResults.content, localSearch]);

    const validateDates = (selectedDate, isReturnDate = false) => {
        if (!selectedDate) return true;

        const currentDate = getCurrentMonthYear();
        const selected = new Date(selectedDate);
        const selectedMonth = selected.getMonth();
        const selectedYear = selected.getFullYear();

        if (isReturnDate) {
            if (filters.departureTime) {
                const departureDate = new Date(filters.departureTime);
                return selected >= departureDate;
            }
            return true;
        }

        return selectedYear > currentDate.year || (selectedYear === currentDate.year && selectedMonth >= currentDate.month);
    };

    const showAlert = async (options) =>
        Swal.fire({
            ...swalConfig,
            ...options
        });

    const handleFilterChange = async (e) => {
        const { name, value } = e.target;
        const isReturnDate = name === 'returnTime';

        if ((name === 'departureTime' || name === 'returnTime') && value && !validateDates(value, isReturnDate)) {
            await showAlert({
                title: 'Invalid Date',
                text: isReturnDate
                    ? 'Return month cannot be before departure month'
                    : 'Cannot select a departure month before the current month',
                icon: 'warning'
            });
            return;
        }

        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleSearch = async (requestedPage = page, requestedRowsPerPage = rowsPerPage) => {
        if (!filters.departureTime && !filters.returnTime && !filters.type) {
            await showAlert({
                title: 'Search Criteria Required',
                text: 'Please select at least one search criteria',
                icon: 'warning'
            });
            return;
        }

        try {
            const searchParams = {
                departureTime: null,
                returnTime: null,
                type: filters.type || null
            };

            if (filters.departureTime) {
                const departureDate = new Date(filters.departureTime);
                departureDate.setDate(1);
                departureDate.setHours(0, 0, 0, 0);
                searchParams.departureTime = departureDate.toISOString();
            }

            if (filters.returnTime) {
                const returnDate = new Date(filters.returnTime);
                returnDate.setMonth(returnDate.getMonth() + 1, 0);
                returnDate.setHours(23, 59, 59, 999);
                searchParams.returnTime = returnDate.toISOString();
            }

            // Check if selected dates are in the past
            if (isTripsInPast(searchParams.departureTime, searchParams.returnTime)) {
                await showAlert({
                    title: 'Invalid Date Range',
                    text: 'Cannot search for trips that have already finished',
                    icon: 'warning'
                });
                return;
            }

            const pageParams = {
                page: requestedPage,
                size: requestedRowsPerPage
            };

            const response = await searchPlans(
                {
                    ...searchParams
                },
                pageParams
            );

            // Filter out past trips from the response
            const filteredResponse = {
                ...response,
                content: filterOutPastTrips(response.content)
            };

            setSearchResults(filteredResponse);
            setSearchPerformed(true);
            setLocalSearch('');

            if (!filteredResponse.content?.length) {
                const result = await showAlert({
                    title: 'No Results Found',
                    text: 'No active trip plans found for the selected criteria. Would you like to create a new plan?',
                    icon: 'info',
                    showCancelButton: true,
                    confirmButtonText: 'Create New Plan',
                    cancelButtonText: 'Continue Searching',
                    confirmButtonColor: globalColor,
                    cancelButtonColor: '#6e7881'
                });

                if (result.isConfirmed) {
                    handleClose();
                    onOpenTravelPlanForm();
                }
            }
        } catch (error) {
            logger.error('Search error:', error);
            await showAlert({
                title: 'Search Failed',
                text: 'An error occurred while searching for travel plans. Please try again.',
                icon: 'error'
            });
        }
    };

    const handleClose = () => {
        setSearchResults({ content: [], totalElements: 0 });
        setSearchPerformed(false);
        setFilters(initialFilters);
        setPage(0);
        setLocalSearch('');
        onClose();
    };

    const handleLocalSearch = (e) => {
        setLocalSearch(e.target.value);
    };

    const handlePageChange = async (event, newPage) => {
        setPage(newPage);
        await handleSearch(newPage, rowsPerPage);
    };

    const handleRowsPerPageChange = async (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0);
        await handleSearch(0, newRowsPerPage);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch {
            return 'Invalid Date';
        }
    };

    const formatDuration = (startDate, endDate) => {
        if (!startDate || !endDate) return 'N/A';
        try {
            const start = new Date(startDate);
            const end = new Date(endDate);
            const diffInDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
            return `${diffInDays} day${diffInDays !== 1 ? 's' : ''}`;
        } catch {
            return 'Invalid Duration';
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    zIndex: 1300,
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: { xs: 'calc(100vh - 16px)', sm: '90vh' },
                    width: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' },
                    maxWidth: { xs: 'none', lg: 1200 },
                    m: { xs: 1, sm: 4 },
                    overflow: 'hidden',
                    '@supports (height: 100dvh)': {
                        maxHeight: { xs: 'calc(100dvh - 16px)', sm: '90vh' }
                    }
                }
            }}
        >
            <DialogTitle sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 }, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    flexDirection={{ xs: 'column', sm: 'row' }}
                    gap={{ xs: 1.25, sm: 2 }}
                >
                    <Typography variant="h6" component="div" sx={{ color: '#141b2d', fontWeight: 700 }}>
                        Search Trip Plans
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => {
                            handleClose();
                            onOpenTravelPlanForm();
                        }}
                        startIcon={<AddIcon />}
                        sx={{
                            bgcolor: globalColor,
                            minHeight: 44,
                            width: { xs: '100%', sm: 'auto' },
                            '&:hover': {
                                bgcolor: 'rgb(0, 100, 0)'
                            }
                        }}
                    >
                        Create New Plan
                    </Button>
                </Box>
            </DialogTitle>

            <DialogContent
                sx={{
                    flex: '1 1 auto',
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    px: { xs: 1.5, sm: 3 },
                    py: { xs: 2, sm: 2.5 },
                    '& .MuiInputBase-root': {
                        minWidth: 0,
                        maxWidth: '100%'
                    },
                    '& input[type="month"]': {
                        minWidth: 0,
                        width: '100%'
                    }
                }}
            >
                <Grid container spacing={{ xs: 1.5, md: 2 }}>
                    <Grid item xs={12} md={4}>
                        <TextField
                            label="Departure Month"
                            type="month"
                            name="departureTime"
                            value={filters.departureTime}
                            onChange={handleFilterChange}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                                min: getCurrentMonthYear().formatted
                            }}
                            sx={{ '& .MuiInputBase-root': { minHeight: 48 } }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            label="Return Month"
                            type="month"
                            name="returnTime"
                            value={filters.returnTime}
                            onChange={handleFilterChange}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                                min: filters.departureTime || getCurrentMonthYear().formatted
                            }}
                            sx={{ '& .MuiInputBase-root': { minHeight: 48 } }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth>
                            <InputLabel>Lunch Type</InputLabel>
                            <Select
                                name="type"
                                value={filters.type}
                                onChange={handleFilterChange}
                                label="Lunch Type"
                                sx={{ minHeight: 48 }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            maxWidth: 'calc(100vw - 24px)',
                                            '& .MuiMenuItem-root': {
                                                whiteSpace: 'normal',
                                                overflowWrap: 'anywhere'
                                            }
                                        }
                                    }
                                }}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="LUNCH_INCLUDED">Lunch is Covered by Host/Organizer</MenuItem>
                                <MenuItem value="LUNCH_EXCLUDED">Need to Claim Lunch Allowance</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>

                <Box mt={{ xs: 1.5, sm: 2 }}>
                    <Button
                        variant="contained"
                        onClick={() => handleSearch()}
                        fullWidth
                        disabled={loading}
                        sx={{
                            bgcolor: globalColor,
                            minHeight: 46,
                            '&:hover': {
                                bgcolor: 'rgb(0, 100, 0)'
                            }
                        }}
                    >
                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Search'}
                    </Button>
                </Box>

                {searchPerformed && (
                    <Box mt={{ xs: 2, sm: 3 }}>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems={{ xs: 'stretch', sm: 'center' }}
                            flexDirection={{ xs: 'column', sm: 'row' }}
                            gap={1.25}
                            mb={2}
                        >
                            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, fontWeight: 700 }}>
                                Search Results ({localSearch ? filteredResults.totalElements : searchResults.totalElements})
                            </Typography>
                            <TextField
                                placeholder="Search in results..."
                                size="small"
                                value={localSearch}
                                onChange={handleLocalSearch}
                                sx={{ width: { xs: '100%', sm: 250 } }}
                            />
                        </Box>

                        {loading ? (
                            <Box display="flex" justifyContent="center" p={3}>
                                <CircularProgress />
                            </Box>
                        ) : (
                            <>
                                <Box sx={{ display: { xs: 'grid', sm: 'none' }, gap: 1.25 }}>
                                    {(localSearch ? filteredResults.content : searchResults.content).map((plan) => (
                                        <Paper
                                            key={plan.id}
                                            variant="outlined"
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 1.5,
                                                borderColor: 'divider',
                                                boxShadow: 'none',
                                                minWidth: 0
                                            }}
                                        >
                                            <Typography
                                                variant="subtitle1"
                                                sx={{ color: '#141b2d', fontWeight: 700, overflowWrap: 'anywhere' }}
                                            >
                                                {plan.planName}
                                            </Typography>
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                                                    gap: 1.25,
                                                    mt: 1.25
                                                }}
                                            >
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Departure
                                                    </Typography>
                                                    <Typography variant="body2">{formatDate(plan.departureTime)}</Typography>
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Return
                                                    </Typography>
                                                    <Typography variant="body2">{formatDate(plan.returnTime)}</Typography>
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Venue
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                                                        {plan.site?.name || 'N/A'}
                                                    </Typography>
                                                </Box>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Duration
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {formatDuration(plan.departureTime, plan.returnTime)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Chip
                                                label={plan.type === 'LUNCH_INCLUDED' ? 'Lunch Covered By Host/Org' : 'Claim Lunch'}
                                                size="small"
                                                color={plan.type === 'LUNCH_INCLUDED' ? 'success' : 'default'}
                                                sx={{
                                                    mt: 1.25,
                                                    maxWidth: '100%',
                                                    height: 'auto',
                                                    minHeight: 24,
                                                    '& .MuiChip-label': {
                                                        whiteSpace: 'normal',
                                                        py: 0.5
                                                    }
                                                }}
                                            />
                                            <Button
                                                variant="contained"
                                                fullWidth
                                                onClick={() => {
                                                    handleClose();
                                                    onOpenTravelRequestForm(plan);
                                                }}
                                                sx={{
                                                    bgcolor: globalColor,
                                                    minHeight: 44,
                                                    mt: 1.5,
                                                    '&:hover': {
                                                        bgcolor: 'rgb(0, 100, 0)'
                                                    }
                                                }}
                                            >
                                                Create Request
                                            </Button>
                                        </Paper>
                                    ))}
                                    {(localSearch ? filteredResults.content : searchResults.content).length === 0 && (
                                        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', boxShadow: 'none' }}>
                                            <Typography color="text.secondary">No results found</Typography>
                                        </Paper>
                                    )}
                                </Box>

                                <TableContainer component={Paper} sx={{ display: { xs: 'none', sm: 'block' } }}>
                                    <Table sx={{ minWidth: 650 }} aria-label="trip plans table">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Plan Name</TableCell>
                                                <TableCell>Departure</TableCell>
                                                <TableCell>Return</TableCell>
                                                <TableCell>Duration</TableCell>
                                                <TableCell>Venue</TableCell>
                                                <TableCell>Lunch Type</TableCell>
                                                <TableCell align="right">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(localSearch ? filteredResults.content : searchResults.content).map((plan) => (
                                                <TableRow
                                                    key={plan.id}
                                                    sx={{
                                                        '&:last-child td, &:last-child th': { border: 0 },
                                                        '&:hover': { backgroundColor: '#f5f5f5' }
                                                    }}
                                                >
                                                    <TableCell component="th" scope="row">
                                                        {plan.planName}
                                                    </TableCell>
                                                    <TableCell>{formatDate(plan.departureTime)}</TableCell>
                                                    <TableCell>{formatDate(plan.returnTime)}</TableCell>
                                                    <TableCell>{formatDuration(plan.departureTime, plan.returnTime)}</TableCell>
                                                    <TableCell>{plan.site?.name || 'N/A'}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={
                                                                plan.type === 'LUNCH_INCLUDED' ? 'Lunch Covered By Host/Org' : 'Claim Lunch'
                                                            }
                                                            size="small"
                                                            color={plan.type === 'LUNCH_INCLUDED' ? 'success' : 'default'}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Button
                                                            variant="contained"
                                                            onClick={() => {
                                                                handleClose();
                                                                onOpenTravelRequestForm(plan);
                                                            }}
                                                            size="small"
                                                            sx={{
                                                                bgcolor: globalColor,
                                                                '&:hover': {
                                                                    bgcolor: 'rgb(0, 100, 0)'
                                                                }
                                                            }}
                                                        >
                                                            Create Request
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            {(localSearch ? filteredResults.content : searchResults.content).length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={7} align="center">
                                                        <Typography variant="body1" sx={{ py: 2 }}>
                                                            No results found
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {searchResults.content.length > 0 && !localSearch && (
                                    <TablePagination
                                        component="div"
                                        count={searchResults.totalElements}
                                        page={page}
                                        onPageChange={handlePageChange}
                                        rowsPerPage={rowsPerPage}
                                        onRowsPerPageChange={handleRowsPerPageChange}
                                        rowsPerPageOptions={[5, 10, 25]}
                                        labelRowsPerPage="Results per page"
                                        sx={{
                                            overflow: 'hidden',
                                            '& .MuiTablePagination-toolbar': {
                                                minHeight: 52,
                                                px: { xs: 0, sm: 2 },
                                                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                                                justifyContent: { xs: 'center', sm: 'flex-end' },
                                                rowGap: 0.5
                                            },
                                            '& .MuiTablePagination-spacer': {
                                                display: { xs: 'none', sm: 'block' }
                                            },
                                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                                m: 0,
                                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                            }
                                        }}
                                    />
                                )}
                            </>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    flex: '0 0 auto',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    p: { xs: 1.5, sm: 2 },
                    pb: { xs: 'calc(12px + env(safe-area-inset-bottom))', sm: 2 }
                }}
            >
                <Button
                    onClick={handleClose}
                    fullWidth
                    sx={{
                        color: 'rgb(128, 128, 128)',
                        minHeight: 44,
                        maxWidth: { sm: 120 },
                        '&:hover': {
                            backgroundColor: 'rgba(128, 128, 128, 0.04)'
                        }
                    }}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

TripPlanSearchDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onOpenTravelPlanForm: PropTypes.func.isRequired,
    onOpenTravelRequestForm: PropTypes.func.isRequired
};

export default TripPlanSearchDialog;
